import { ApiError } from "./api";

/**
 * What the Question route should do about a failed question fetch.
 *
 * - `bounce`            — stale tab, the attempt is fine: send them to the gate.
 * - `break`             — the attempt is paused for a between-section break (CP-18):
 *                         rebuild the break screen from `pausedSectionOf(err)`.
 * - `done`              — the attempt is finalized: hand off to the Done screen.
 * - `clear-and-bounce`  — the STORED attempt id is unusable: drop it, then bounce.
 * - `show-error`        — anything else: render the failure card and let them retry.
 */
export type QuestionErrorAction =
  | "bounce"
  | "break"
  // EF-2 — the attempt is paused (`attempt_paused`): show the paused screen,
  // rebuilt from `pausedAttemptOf(err)`.
  | "paused"
  | "done"
  | "clear-and-bounce"
  | "show-error";

/** What `_attempt_paused_error` puts on the 409 (EF-2), so a stale tab or the
 *  gate's stored-id resume (which always tries question 1) can render the
 *  paused screen without router state. Null when the detail is not one. */
export interface PausedAttempt {
  position: number;
  paused_at: string | null;
  resume_by: string | null;
  remaining_seconds: number | null;
}

export function pausedAttemptOf(err: unknown): PausedAttempt | null {
  if (!(err instanceof ApiError)) return null;
  const detail: unknown = err.detail;
  if (!detail || typeof detail !== "object") return null;
  const d = detail as Record<string, unknown>;
  if (d.reason !== "attempt_paused" || typeof d.position !== "number") {
    return null;
  }
  return {
    position: d.position,
    paused_at: typeof d.paused_at === "string" ? d.paused_at : null,
    resume_by: typeof d.resume_by === "string" ? d.resume_by : null,
    remaining_seconds:
      typeof d.remaining_seconds === "number" ? d.remaining_seconds : null,
  };
}

/** The finished section's bounds, as `_section_paused_error` puts them on the
 *  409 so a stale tab / the gate's question-1 resume can render the break
 *  screen without router state. Null when the detail carries no section. */
export interface PausedSection {
  index: number;
  start_position: number;
  end_position: number;
  is_last: boolean;
}

export function pausedSectionOf(err: unknown): PausedSection | null {
  if (!(err instanceof ApiError)) return null;
  const detail: unknown = err.detail;
  if (!detail || typeof detail !== "object") return null;
  const sec: unknown = (detail as { section?: unknown }).section;
  if (!sec || typeof sec !== "object") return null;
  const s = sec as Record<string, unknown>;
  if (
    typeof s.index !== "number" ||
    typeof s.start_position !== "number" ||
    typeof s.end_position !== "number" ||
    typeof s.is_last !== "boolean"
  ) {
    return null;
  }
  return {
    index: s.index,
    start_position: s.start_position,
    end_position: s.end_position,
    is_last: s.is_last,
  };
}

/**
 * Exact backend detail for "this token has no such attempt"
 * (`_get_attempt_for_token`, backend/api/candidate.py:191). Pinned as a constant
 * because it is an ALLOWLIST: see the 404 branch below for why a rename must
 * degrade to `show-error` rather than to `clear-and-bounce`.
 */
const ATTEMPT_NOT_FOUND = "Attempt not found";

/**
 * 10-07 S11: the server no longer knows this attempt for this token — voided
 * before submit (an invitation replaced this public-link attempt, or a retake),
 * erased, or a rotated invitation. Every candidate route answers this same
 * 404, so the stored id is junk: drop it and run the Gate again. The same
 * ALLOWLIST as `classifyQuestionError`'s 404 branch — any other 404 describes
 * the request and leaves a live attempt behind.
 */
export function isAttemptGone(err: unknown): boolean {
  return (
    err instanceof ApiError && err.status === 404 && err.detail === ATTEMPT_NOT_FOUND
  );
}

/** FastAPI's structured errors carry a machine-readable `detail.reason`. */
function reasonOf(detail: unknown): unknown {
  return detail && typeof detail === "object"
    ? (detail as { reason?: unknown }).reason
    : undefined;
}

/** FastAPI request-validation entries look like `{loc: ["header", "X-Attempt-Id"], ...}`. */
function mentionsAttemptIdHeader(detail: unknown): boolean {
  if (!Array.isArray(detail)) return false;
  return detail.some((entry) => {
    const loc: unknown = (entry as { loc?: unknown })?.loc;
    return (
      Array.isArray(loc) &&
      loc.some((p) => String(p).toLowerCase() === "x-attempt-id")
    );
  });
}

/**
 * Classify a `GET /api/t/{token}/question/{position}` failure.
 *
 * Replaces substring matching on the formatted `API <status>: <body>` message,
 * which was the shared root cause of two verified candidate-lockout bugs
 * (2026-08-26 verification of FRONTEND-REACT-8):
 *
 *   - `msg.includes("404")` also matched `404 "Question position out of range"`,
 *     so opening a stale/mistyped `/question/99` during a HEALTHY live attempt
 *     cleared the only handle to it — after which `/start` answers 409 "already
 *     in progress" and the candidate is locked out for good.
 *   - `msg.includes("409")` matched any body that merely CONTAINED "409".
 *
 * Reading `err.status` / `err.detail` off ApiError removes both. Order matters:
 * a `section_locked` 409 must be recognised before the generic 409/410 → Done.
 */
export function classifyQuestionError(err: unknown): QuestionErrorAction {
  // Network failures, aborts, JSON parse errors: no status to reason about, and
  // refreshing may well fix them. Never touch stored state on these.
  if (!(err instanceof ApiError)) return "show-error";

  const detail: unknown = err.detail;
  const reason = reasonOf(detail);

  if (err.status === 409 || err.status === 410) {
    // CP-18: every position is closed while the candidate is on a break. The
    // attempt is very much alive — show the break screen, never Done.
    if (reason === "section_paused") {
      return "break";
    }
    // EF-2: the candidate paused the test. Alive, frozen clock — the paused
    // screen, never Done.
    if (reason === "attempt_paused") {
      return "paused";
    }
    // A locked/not-started section means the tab is stale (a bookmarked question
    // in a section they've left or not reached), NOT that the attempt is over.
    if (reason === "section_locked" || reason === "section_not_started") {
      return "bounce";
    }
    return "done";
  }

  if (err.status === 404) {
    // ALLOWLIST, deliberately. The server does not recognise this attempt for
    // this token: voided by allow-retake, erased under GDPR Art.17, or the id
    // belongs to a rotated invitation. Only then is the stored id junk.
    //
    // Every other 404 on this route describes the REQUEST, not the attempt
    // ("Question position out of range", "Test not found") and leaves a
    // perfectly live attempt behind. The two mistakes are not symmetric: failing
    // to clear a dead id costs one bounce through the normal gate flow, while
    // clearing a live one locks the candidate out of an assessment. So an
    // unrecognised 404 must fall through to `show-error`.
    return detail === ATTEMPT_NOT_FOUND ? "clear-and-bounce" : "show-error";
  }

  if (err.status === 422 && mentionsAttemptIdHeader(detail)) {
    // The stored id is not even a UUID, so FastAPI rejected the header before
    // any lookup. It can never become valid — keeping it traps the candidate on
    // the failure card forever, with "Please refresh the page" as the only
    // (useless) advice. loadAttemptId() now screens these out upstream; this is
    // the backstop for anything that slips past it.
    return "clear-and-bounce";
  }

  return "show-error";
}

/**
 * What the Question route should do when a mid-test TRANSITION fails.
 *
 * - `section-transition` — an earlier section's clock lapsed: advance, keep going.
 * - `done`               — the attempt is finalized or over: converge to the Done screen.
 * - `stay`               — anything else: leave the candidate on the question.
 * - `clear-and-bounce`   — the attempt is gone (`isAttemptGone`): drop the id, back to the Gate.
 */
export type AdvanceErrorAction =
  | "section-transition"
  | "done"
  | "stay"
  | "clear-and-bounce";

/**
 * Classify a failure thrown out of the Question route's `advance` transition.
 *
 * NAMED FOR WHAT IT ACTUALLY SEES. The catch this feeds was written as (and
 * commented as) an answer-save handler, but a failed `POST /answer` can never
 * reach it: `submitAnswer` is only ever called inside `enqueueAnswerSave`, and
 * `answerQueue.ts` swallows a permanently-failed save (`.catch(() => {})`) so the
 * chain keeps draining — which also means `flushAnswerSaves()` never rejects.
 * What the catch really receives is the awaited transition: `submitAttempt` or
 * `advanceSection`.
 *
 * Two hazards this removes, both latent until 2026-08-26:
 *
 *   - `msg.includes("409")` matched any response BODY that merely contained
 *     "409" (an id, a message fragment, a 5xx echoing an upstream status), which
 *     would send a candidate with a live attempt to the Done screen. `submit`
 *     legitimately 409s `required_unanswered` / `sections_remaining`, so bodies
 *     with structured detail really do flow through here.
 *     `answerQueue.ts`'s `isTerminal` already warns about exactly this.
 *   - `msg.includes("section_time_up")` read a machine-readable reason out of
 *     free text, so any body quoting the phrase would trigger a section
 *     transition.
 */
export function classifyAdvanceError(err: unknown): AdvanceErrorAction {
  // Network failures, aborts, timeouts: the attempt is not known to be over, so
  // never move the candidate off their question.
  if (!(err instanceof ApiError)) return "stay";

  // ORDER IS LOAD-BEARING, and this branch is kept deliberately even though it
  // is currently UNREACHABLE. `section_time_up` is the non-terminal 410 raised
  // by `_enforce_deadline` for a non-last section — but only on a write that
  // passes a `position`, i.e. `POST /answer`, whose errors the queue swallows.
  // Neither awaited transition can raise it today (`/submit` calls
  // `_enforce_deadline` WITHOUT a position, so it can only produce the terminal
  // `time_limit_reached`; `/advance-section` does not call it at all).
  //
  // Kept, not deleted, because the risk is asymmetric: if the save path ever
  // becomes awaited, or `/advance-section` starts enforcing the deadline, the
  // generic 410 below would dump a sectioned candidate on the Done screen
  // mid-assessment. Correct-and-dormant costs nothing; wrong-and-live costs an
  // assessment. The terminal deadline is a DIFFERENT 410
  // (`reason: "time_limit_reached"`) and must keep falling through to `done` —
  // see `parseTimeLimitReached` in api.ts.
  if (err.status === 410 && reasonOf(err.detail) === "section_time_up") {
    return "section-transition";
  }

  // FEAT-11: the same asymmetry, for a reason that IS reachable. A mixed
  // attempt's technical clock lapsing answers `technical_time_up` — a 410 with
  // a live attempt behind it, because the clockless questionnaire is still
  // owed. Falling through to the generic `done` below would strand the
  // candidate on the Done screen with the profile uncollected, which is the
  // exact outcome making the lapse non-terminal exists to prevent. `stay` is
  // right rather than a transition: the Question route's own `handleTimeUp`
  // owns the navigation into the questionnaire, so this must only avoid
  // moving the candidate anywhere.
  if (err.status === 410 && reasonOf(err.detail) === "technical_time_up") {
    return "stay";
  }

  // CP-18: a transition refused because the attempt is paused for a break, or
  // because breaks aren't enabled on this test, leaves a LIVE attempt behind.
  // Converging to Done here would strand the candidate mid-assessment.
  if (err.status === 409) {
    const reason = reasonOf(err.detail);
    if (reason === "section_paused" || reason === "breaks_disabled") return "stay";
    // EF-2: same shape — a paused attempt, or a pause refused because the
    // test has none, is a LIVE attempt. (The Question route checks
    // `pausedAttemptOf` before this and routes to the paused screen itself.)
    if (reason === "attempt_paused" || reason === "pause_unavailable") return "stay";
    // FEAT-11: the questionnaire is a locked following block until the
    // questions are answered or out of time, and the technical half is closed
    // once a statement is answered. Both leave a LIVE attempt behind.
    if (reason === "questions_not_finished" || reason === "questionnaire_started")
      return "stay";
  }

  // Every other 409/410 means the attempt is finalized, expired, or otherwise
  // refusing the transition. Preserves the behaviour this path has always had —
  // including for `required_unanswered` and `sections_remaining`, which are
  // normally pre-empted by a disabled button.
  if (err.status === 409 || err.status === 410) return "done";

  // 10-07 S11: voided / erased — staying would leave a Submit that can never
  // succeed. Only the exact "Attempt not found" detail (see `isAttemptGone`).
  if (isAttemptGone(err)) return "clear-and-bounce";

  // 5xx, any other 404, 422: recoverable or the candidate's own input. Stay put.
  return "stay";
}

/**
 * OPS-28 #2: /submit refused because a section is still owed (409
 * `sections_remaining`). A LIVE attempt — the candidate reached Done early
 * (a blip on the time-up section transition), and the way back in is the
 * gate, which resumes the attempt at its next section. Never `done`.
 */
export function isSectionsRemaining(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    err.status === 409 &&
    reasonOf(err.detail) === "sections_remaining"
  );
}

/**
 * Is this error a terminal "attempt already finalized" signal (409/410)?
 *
 * Shared by SectionReview's review-fetch guard (`attemptFinalized`) and its
 * continue-transition handler. Replaces `errorMessage.includes("409"/"410")`,
 * which matched any response body that merely CONTAINED those digits — attempt
 * ids are hex UUIDs, so "409"/"410" are realistic substrings, and a non-terminal
 * 5xx/404 echoing one would strand a live candidate on the Done screen. Classify
 * on status, never on text.
 *
 * NOTE: the Review *submit* path deliberately uses a 410-ONLY check, not this
 * helper — a 409 `required_unanswered` there must refetch to refresh the pills,
 * not converge to Done. See `Review.tsx`'s `submitMutation.onError`.
 */
export function isFinalizedAttempt(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  // EF-2: a paused attempt answers 409 on /review too, and it is the one 409
  // there that leaves a LIVE attempt behind — the review screens route it to
  // the paused screen instead (see `pausedAttemptOf`).
  if (err.status === 409 && reasonOf(err.detail) === "attempt_paused") return false;
  return err.status === 409 || err.status === 410;
}

/**
 * What the Gate should show when `POST /start` fails.
 *
 * Replaces the `"detail":"…"` regex the Gate used to scrape off the formatted
 * message, which rendered any STRUCTURED detail — the invitation-identity 409,
 * and now CP-16's `email_undeliverable` 400 — as raw JSON. Same rule as the
 * classifiers above: read `status` / `detail` off ApiError, never the text.
 *
 * `field: "email"` asks the Gate to attach the message to the Email input
 * (inline error, focus) instead of the generic box — the one start failure a
 * candidate fixes by editing a field. Everything else, including FastAPI's
 * 422 list and network failures, degrades to a plain sentence.
 */
export type StartErrorDescription = { message: string; field?: "email" };

const GENERIC_START_ERROR = "Could not start the test. Please try again.";

/** EP-20: the neutral decline message when `err` is the `screening_declined`
 *  refusal (a knock-off answer at /start, a re-entry after one, or the 410
 *  backstop), else null. Checked BEFORE `describeStartError`, which would
 *  otherwise render the message as an inline form error the candidate can
 *  "fix" by resubmitting. */
export function screeningDeclinedMessage(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  if (err.status !== 409 && err.status !== 410) return null;
  if (reasonOf(err.detail) !== "screening_declined") return null;
  const message: unknown = (err.detail as { message?: unknown }).message;
  return typeof message === "string" && message ? message : null;
}

/** OPS-22: slowapi's 429 body is `{"error": "…"}` — no `detail` — so without
 *  this branch an office full of colleagues starting at once read the generic
 *  "could not start" and had no idea a minute's wait fixes it. Placeholder
 *  wording (Emilia's call), keyed on the status, never the text. */
const START_RATE_LIMITED =
  "Lots of people are starting right now — please try again in a minute.";

export function describeStartError(err: unknown): StartErrorDescription {
  if (!(err instanceof ApiError)) return { message: GENERIC_START_ERROR };
  if (err.status === 429) return { message: START_RATE_LIMITED };
  const detail: unknown = err.detail;
  if (typeof detail === "string" && detail) return { message: detail };
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const message: unknown = (detail as { message?: unknown }).message;
    if (typeof message === "string" && message) {
      return reasonOf(detail) === "email_undeliverable"
        ? { message, field: "email" }
        : { message };
    }
  }
  return { message: GENERIC_START_ERROR };
}
